/**
 * Firestore Messages Service
 * 
 * Handles all message-related operations:
 * - Send messages (text, image, voice)
 * - Fetch messages with pagination
 * - Real-time subscriptions
 * - Reactions
 * - Delete messages
 * - Mark as read
 * - Rate limiting (15 messages/second/user)
 */

import {
  collection,
  doc,
  addDoc,
  getDoc,
  getDocs,
  updateDoc,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  onSnapshot,
  serverTimestamp,
  arrayUnion,
  arrayRemove,
  runTransaction,
  Timestamp,
  FieldPath
} from 'firebase/firestore';
import { getFirestoreDb } from '../firebase';
import { 
  COLLECTIONS, 
  MESSAGE_TYPES, 
  MESSAGE_STATUS, 
  PAGE_SIZE,
  DELETE_FOR_EVERYONE_WINDOW,
  sanitizeAvatarUrl
} from './schema';
import { updateLastMessage, getConversation } from './conversations';

// Strip HTML tags for plain-text contexts (notifications, previews)
function stripHtml(html) {
  if (!html) return '';
  return html
    .replace(/<br\s*\/?>/gi, ' ')
    .replace(/<\/p><p>/gi, ' ')
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/\s+/g, ' ')
    .trim();
}

const db = () => getFirestoreDb();
const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Rate Limiting for Messages
 * Prevents spam by limiting to 15 messages per second per user
 */
const MESSAGE_RATE_LIMIT = {
  maxMessages: 15,
  windowMs: 1000 // 1 second
};

// Track message timestamps per user (in-memory)
const messageTimestamps = new Map();

/**
 * Check if user can send a message (rate limiting)
 * @param {string} userId - User ID to check
 * @returns {boolean} - True if allowed, false if rate limited
 */
function checkMessageRateLimit(userId) {
  if (!userId) return true;
  
  const now = Date.now();
  const timestamps = messageTimestamps.get(userId) || [];
  
  // Filter to only timestamps within the window
  const recentTimestamps = timestamps.filter(ts => now - ts < MESSAGE_RATE_LIMIT.windowMs);
  
  if (recentTimestamps.length >= MESSAGE_RATE_LIMIT.maxMessages) {
    console.warn(`[RateLimit] User ${userId} exceeded message rate limit`);
    return false;
  }
  
  // Add current timestamp and update map
  recentTimestamps.push(now);
  messageTimestamps.set(userId, recentTimestamps);
  
  // Clean up old entries periodically (every 100 messages)
  if (messageTimestamps.size > 100) {
    for (const [uid, ts] of messageTimestamps.entries()) {
      const recent = ts.filter(t => now - t < MESSAGE_RATE_LIMIT.windowMs * 10);
      if (recent.length === 0) {
        messageTimestamps.delete(uid);
      } else {
        messageTimestamps.set(uid, recent);
      }
    }
  }
  
  return true;
}

/**
 * Create DM notification in Supabase for all recipients
 */
async function createDMNotification(conversationId, messageId, sender, preview, recipients) {
  if (!API_URL) return;
  
  try {
    const results = await Promise.allSettled(
      recipients.map(async (recipientId) => {
        const options = {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            recipient_id: recipientId,
            sender_id: sender.id,
            sender_name: sender.name || sender.email,
            sender_avatar: sender.avatar || null,
            conversation_id: conversationId,
            message_id: messageId,
            preview: preview,
            church_id: sender.church_id || null
          })
        };
        try {
          const res = await fetch(`${API_URL}/api/comms/v3/notifications/dm`, options);
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
        } catch {
          // One retry on any failure (network error or non-ok response)
          await fetch(`${API_URL}/api/comms/v3/notifications/dm`, options).catch(() => {});
        }
      })
    );
    // Log any individual failures without blocking others
    results.forEach((r, i) => {
      if (r.status === 'rejected') {
        console.error(`DM notification failed for recipient ${recipients[i]}:`, r.reason);
      }
    });
  } catch (err) {
    console.error('Error creating DM notification:', err);
  }
}

/**
 * Get messages subcollection reference
 */
function getMessagesRef(conversationId) {
  return collection(db(), COLLECTIONS.CONVERSATIONS, conversationId, COLLECTIONS.MESSAGES);
}

/**
 * Send a text message
 */
export async function sendTextMessage(conversationId, sender, text, replyTo = null) {
  // Check rate limit
  if (!checkMessageRateLimit(sender.id)) {
    throw new Error('Rate limit exceeded. Please wait a moment before sending more messages.');
  }
  
  const messagesRef = getMessagesRef(conversationId);
  
  const messageData = {
    text: text.trim(),
    senderId: sender.id,
    senderName: sender.name || sender.email,
    senderAvatar: sanitizeAvatarUrl(sender.avatar) || null,
    type: MESSAGE_TYPES.TEXT,
    createdAt: serverTimestamp(),
    status: MESSAGE_STATUS.SENT,
    readBy: [sender.id],
    reactions: {},
    deletedFor: [],
    deletedForEveryone: false,
    ...(replyTo ? { replyTo } : {})
  };
  
  // This is the critical operation - if this succeeds, the message is sent
  const docRef = await addDoc(messagesRef, messageData);
  
  // Everything below is non-critical - don't let failures affect the success response
  // Update conversation's last message (fire and forget)
  updateLastMessage(conversationId, messageData).catch(err => {
    console.error('Error updating last message:', err);
  });
  
  // Create notifications for other participants (fire and forget)
  // Unread counts are now handled server-side in /notifications/dm
  (async () => {
    try {
      const conversation = await getConversation(conversationId);
      if (conversation && conversation.participants) {
        const otherParticipants = conversation.participants.filter(id => id !== sender.id);
        // Create DM notifications for recipients
        createDMNotification(conversationId, docRef.id, sender, stripHtml(text).trim(), otherParticipants);
      }
    } catch (err) {
      console.error('Error with post-send operations:', err);
    }
  })();
  
  return { id: docRef.id, ...messageData };
}

/**
 * Send an image message (optionally with caption text)
 */
export async function sendImageMessage(conversationId, sender, images, caption = '', replyTo = null) {
  // Check rate limit
  if (!checkMessageRateLimit(sender.id)) {
    throw new Error('Rate limit exceeded. Please wait a moment before sending more messages.');
  }
  
  const messagesRef = getMessagesRef(conversationId);
  const trimmedCaption = caption ? caption.trim() : '';
  
  const messageData = {
    text: trimmedCaption,
    senderId: sender.id,
    senderName: sender.name || sender.email,
    senderAvatar: sanitizeAvatarUrl(sender.avatar) || null,
    type: MESSAGE_TYPES.IMAGE,
    images: images, // Array of { url, width, height }
    createdAt: serverTimestamp(),
    status: MESSAGE_STATUS.SENT,
    readBy: [sender.id],
    reactions: {},
    deletedFor: [],
    deletedForEveryone: false,
    ...(replyTo ? { replyTo } : {})
  };
  
  // Critical operation - if this succeeds, the message is sent
  const docRef = await addDoc(messagesRef, messageData);
  
  // Non-critical operations - fire and forget
  updateLastMessage(conversationId, messageData).catch(err => {
    console.error('Error updating last message:', err);
  });
  
  // Create DM notifications for recipients (fire and forget)
  const notificationPreview = trimmedCaption ? `📷 ${trimmedCaption}` : '📷 Image';
  (async () => {
    try {
      const conversation = await getConversation(conversationId);
      if (conversation && conversation.participants) {
        const otherParticipants = conversation.participants.filter(id => id !== sender.id);
        createDMNotification(conversationId, docRef.id, sender, notificationPreview, otherParticipants);
      }
    } catch (err) {
      console.error('Error creating image message notification:', err);
    }
  })();
  
  return { id: docRef.id, ...messageData };
}

/**
 * Send a voice memo message
 */
export async function sendVoiceMessage(conversationId, sender, voiceMemo, replyTo = null) {
  // Check rate limit
  if (!checkMessageRateLimit(sender.id)) {
    throw new Error('Rate limit exceeded. Please wait a moment before sending more messages.');
  }
  
  const messagesRef = getMessagesRef(conversationId);
  
  const messageData = {
    text: '',
    senderId: sender.id,
    senderName: sender.name || sender.email,
    senderAvatar: sanitizeAvatarUrl(sender.avatar) || null,
    type: MESSAGE_TYPES.VOICE,
    voiceMemo: voiceMemo, // { url, duration }
    createdAt: serverTimestamp(),
    status: MESSAGE_STATUS.SENT,
    readBy: [sender.id],
    reactions: {},
    deletedFor: [],
    deletedForEveryone: false,
    ...(replyTo ? { replyTo } : {})
  };
  
  // Critical operation - if this succeeds, the message is sent
  const docRef = await addDoc(messagesRef, messageData);
  
  // Non-critical operations - fire and forget
  updateLastMessage(conversationId, messageData).catch(err => {
    console.error('Error updating last message:', err);
  });
  
  // Create DM notifications for recipients (fire and forget)
  (async () => {
    try {
      const conversation = await getConversation(conversationId);
      if (conversation && conversation.participants) {
        const otherParticipants = conversation.participants.filter(id => id !== sender.id);
        createDMNotification(conversationId, docRef.id, sender, '🎤 Voice memo', otherParticipants);
      }
    } catch (err) {
      console.error('Error creating voice message notification:', err);
    }
  })();
  
  return { id: docRef.id, ...messageData };
}

/**
 * Send a video message
 */
export async function sendVideoMessage(conversationId, sender, video, caption = '', replyTo = null) {
  if (!checkMessageRateLimit(sender.id)) {
    throw new Error('Rate limit exceeded. Please wait a moment before sending more messages.');
  }

  const messagesRef = getMessagesRef(conversationId);
  const trimmedCaption = caption ? caption.trim() : '';

  const messageData = {
    text: trimmedCaption,
    senderId: sender.id,
    senderName: sender.name || sender.email,
    senderAvatar: sanitizeAvatarUrl(sender.avatar) || null,
    type: MESSAGE_TYPES.VIDEO,
    video: video, // { url, thumbnailUrl, duration, width, height }
    createdAt: serverTimestamp(),
    status: MESSAGE_STATUS.SENT,
    readBy: [sender.id],
    reactions: {},
    deletedFor: [],
    deletedForEveryone: false,
    ...(replyTo ? { replyTo } : {})
  };

  const docRef = await addDoc(messagesRef, messageData);

  updateLastMessage(conversationId, messageData).catch(err => {
    console.error('Error updating last message:', err);
  });

  const notificationPreview = trimmedCaption ? `🎬 ${trimmedCaption}` : '🎬 Video';
  (async () => {
    try {
      const conversation = await getConversation(conversationId);
      if (conversation && conversation.participants) {
        const otherParticipants = conversation.participants.filter(id => id !== sender.id);
        createDMNotification(conversationId, docRef.id, sender, notificationPreview, otherParticipants);
      }
    } catch (err) {
      console.error('Error creating video message notification:', err);
    }
  })();

  return { id: docRef.id, ...messageData };
}

/**
 * Send a poll message (DM/group). The poll lives natively on the Firestore
 * message doc, so votes ride the same realtime onSnapshot path as read receipts.
 *
 * @param {Object} poll - { question, options: [{text}] | [string], allow_other }
 */
export async function sendPollMessage(conversationId, sender, poll, replyTo = null) {
  if (!checkMessageRateLimit(sender.id)) {
    throw new Error('Rate limit exceeded. Please wait a moment before sending more messages.');
  }

  const messagesRef = getMessagesRef(conversationId);

  // Normalize into the exact shape the feed poll uses: options carry their own
  // votes array, plus a free-text other_responses bucket.
  const normalizedPoll = {
    question: (poll.question || '').trim(),
    options: (poll.options || [])
      .map(o => (typeof o === 'string' ? { text: o, votes: [] } : { text: o.text, votes: Array.isArray(o.votes) ? o.votes : [] }))
      .filter(o => o.text && o.text.trim().length > 0),
    allow_other: !!poll.allow_other,
    allow_multiple: !!poll.allow_multiple,
    other_responses: []
  };

  const messageData = {
    text: '',
    senderId: sender.id,
    senderName: sender.name || sender.email,
    senderAvatar: sanitizeAvatarUrl(sender.avatar) || null,
    type: MESSAGE_TYPES.POLL,
    poll: normalizedPoll,
    createdAt: serverTimestamp(),
    status: MESSAGE_STATUS.SENT,
    readBy: [sender.id],
    reactions: {},
    deletedFor: [],
    deletedForEveryone: false,
    ...(replyTo ? { replyTo } : {})
  };

  const docRef = await addDoc(messagesRef, messageData);

  updateLastMessage(conversationId, messageData).catch(err => {
    console.error('Error updating last message:', err);
  });

  const notificationPreview = `📊 Poll: ${normalizedPoll.question}`;
  (async () => {
    try {
      const conversation = await getConversation(conversationId);
      if (conversation && conversation.participants) {
        const otherParticipants = conversation.participants.filter(id => id !== sender.id);
        createDMNotification(conversationId, docRef.id, sender, notificationPreview, otherParticipants);
      }
    } catch (err) {
      console.error('Error creating poll message notification:', err);
    }
  })();

  return { id: docRef.id, ...messageData };
}

/**
 * Vote on a DM poll option. Runs in a transaction so concurrent voters can't
 * clobber each other. Mirrors the feed semantics: a voter is removed from every
 * option (and from other_responses) before being added to their chosen option.
 */
export async function voteOnDmPoll(conversationId, messageId, userId, optionIndex) {
  const ref = doc(db(), COLLECTIONS.CONVERSATIONS, conversationId, COLLECTIONS.MESSAGES, messageId);
  // Capture the committed poll so we can return it. runTransaction may run the
  // callback multiple times on contention; this is reassigned each attempt, so
  // it holds the value from the run that actually committed.
  let updatedPoll;
  await runTransaction(db(), async (tx) => {
    const snap = await tx.get(ref);
    if (!snap.exists()) throw new Error('Poll not found');
    const poll = snap.data().poll;
    if (!poll || !Array.isArray(poll.options)) throw new Error('Message is not a poll');

    const allowMultiple = !!poll.allow_multiple;
    const options = poll.options.map(o => (
      typeof o === 'object' ? { ...o, votes: [...(o.votes || [])] } : { text: o, votes: [] }
    ));

    if (allowMultiple) {
      // Toggle ONLY the clicked option; leave the others + other_responses intact.
      if (optionIndex >= 0 && optionIndex < options.length) {
        const votes = options[optionIndex].votes;
        const i = votes.indexOf(userId);
        if (i !== -1) votes.splice(i, 1); else votes.push(userId);
      }
      updatedPoll = { ...poll, options };
    } else {
      // Single-select: remove from every option (+ other_responses), then add.
      options.forEach(opt => { opt.votes = opt.votes.filter(id => id !== userId); });
      const other_responses = (poll.other_responses || []).filter(r => r.user_id !== userId);
      if (optionIndex >= 0 && optionIndex < options.length) {
        options[optionIndex].votes.push(userId);
      }
      updatedPoll = { ...poll, options, other_responses };
    }
    tx.update(ref, { poll: updatedPoll });
  });
  // Returning the authoritative poll lets <PollView> reconcile its optimistic
  // state immediately (same as the feed), instead of waiting on the onSnapshot
  // round-trip — closes a rare flicker if a read-receipt snapshot lands mid-vote.
  return updatedPoll;
}

/**
 * Submit a free-text ("Other") response to a DM poll. Same remove-then-add
 * semantics in a transaction.
 */
export async function voteOnDmPollOther(conversationId, messageId, userId, responseText) {
  const text = (responseText || '').trim();
  if (!text) return;
  const ref = doc(db(), COLLECTIONS.CONVERSATIONS, conversationId, COLLECTIONS.MESSAGES, messageId);
  let updatedPoll;
  await runTransaction(db(), async (tx) => {
    const snap = await tx.get(ref);
    if (!snap.exists()) throw new Error('Poll not found');
    const poll = snap.data().poll;
    if (!poll || !Array.isArray(poll.options)) throw new Error('Message is not a poll');
    if (!poll.allow_other) throw new Error('This poll does not allow other responses');

    const allowMultiple = !!poll.allow_multiple;
    const options = poll.options.map(o => {
      const opt = typeof o === 'object' ? { ...o, votes: [...(o.votes || [])] } : { text: o, votes: [] };
      // Multi-select: "Other" is an additional pick — keep option selections.
      if (!allowMultiple) opt.votes = opt.votes.filter(id => id !== userId);
      return opt;
    });
    const other_responses = (poll.other_responses || []).filter(r => r.user_id !== userId);
    other_responses.push({ user_id: userId, response: text });

    updatedPoll = { ...poll, options, other_responses };
    tx.update(ref, { poll: updatedPoll });
  });
  return updatedPoll;
}



/**
 * Add a system message (user joined, left, etc.)
 */
export async function addSystemMessage(conversationId, { subtype, text, userId }) {
  const messagesRef = getMessagesRef(conversationId);
  
  const messageData = {
    text,
    senderId: userId,
    senderName: 'System',
    type: MESSAGE_TYPES.SYSTEM,
    subtype,
    createdAt: serverTimestamp(),
    readBy: [],
    reactions: {},
    deletedFor: [],
    deletedForEveryone: false
  };
  
  const docRef = await addDoc(messagesRef, messageData);
  return { id: docRef.id, ...messageData };
}

/**
 * Get messages for a conversation with pagination
 */
export async function getMessages(conversationId, lastDoc = null, pageSize = PAGE_SIZE.MESSAGES) {
  const messagesRef = getMessagesRef(conversationId);
  
  let q = query(
    messagesRef,
    orderBy('createdAt', 'desc'),
    limit(pageSize)
  );
  
  if (lastDoc) {
    q = query(
      messagesRef,
      orderBy('createdAt', 'desc'),
      startAfter(lastDoc),
      limit(pageSize)
    );
  }
  
  const snapshot = await getDocs(q);
  const messages = snapshot.docs.map(doc => ({
    id: doc.id,
    // 'estimate' resolves a pending serverTimestamp() to the local commit time
    // instead of null, so a just-sent message sorts/renders as the newest record.
    ...doc.data({ serverTimestamps: 'estimate' }),
    _doc: doc // Keep reference for pagination
  }));
  
  return {
    messages: messages.reverse(), // Return in chronological order
    lastDoc: snapshot.docs[snapshot.docs.length - 1] || null,
    hasMore: snapshot.docs.length === pageSize
  };
}

/**
 * Subscribe to real-time message updates for a conversation
 */
export function subscribeToMessages(conversationId, callback, limitCount = PAGE_SIZE.MESSAGES) {
  const messagesRef = getMessagesRef(conversationId);
  const q = query(
    messagesRef,
    orderBy('createdAt', 'desc'),
    limit(limitCount)
  );
  
  return onSnapshot(q, (snapshot) => {
    const messages = snapshot.docs
      .map(doc => ({
        id: doc.id,
        // 'estimate' resolves a pending serverTimestamp() to the local commit
        // time instead of null. Without it, a message you just sent has
        // createdAt === null on the local-write snapshot, which downstream sorts
        // treat as epoch (1970) and yank to the top of the thread — making it
        // look like the message vanished from the bottom until a fresh load.
        ...doc.data({ serverTimestamps: 'estimate' })
      }))
      .reverse(); // Chronological order
    
    callback(messages);
  }, (error) => {
    console.error('Error subscribing to messages:', error);
  });
}

/**
 * Mark messages as read by a user (batched write for efficiency)
 */
export async function markMessagesAsRead(conversationId, messageIds, userId) {
  const { writeBatch } = await import('firebase/firestore');
  
  // Firestore batches have a 500 operation limit
  for (let i = 0; i < messageIds.length; i += 500) {
    const chunk = messageIds.slice(i, i + 500);
    const batch = writeBatch(db());
    for (const messageId of chunk) {
      const docRef = doc(db(), COLLECTIONS.CONVERSATIONS, conversationId, COLLECTIONS.MESSAGES, messageId);
      batch.update(docRef, { readBy: arrayUnion(userId) });
    }
    await batch.commit();
  }
}

/**
 * Mark all messages in a conversation as read (batched write)
 */
export async function markConversationAsRead(conversationId, userId) {
  const { writeBatch } = await import('firebase/firestore');
  const messagesRef = getMessagesRef(conversationId);
  
  const recentQuery = query(messagesRef, orderBy('createdAt', 'desc'), limit(100));
  const snapshot = await getDocs(recentQuery);
  
  const unreadDocs = snapshot.docs.filter(d => !d.data().readBy?.includes(userId));
  
  for (let i = 0; i < unreadDocs.length; i += 500) {
    const chunk = unreadDocs.slice(i, i + 500);
    const batch = writeBatch(db());
    for (const d of chunk) {
      batch.update(d.ref, { readBy: arrayUnion(userId) });
    }
    await batch.commit();
  }
}

/**
 * Create notification for message reaction
 */
async function createMessageReactionNotification(
  conversationId, 
  messageId, 
  emoji, 
  reactorId, 
  reactorName, 
  reactorAvatar,
  messageAuthorId,
  messagePreview,
  churchId
) {
  if (!API_URL) return;
  
  // Don't notify if reacting to own message
  if (reactorId === messageAuthorId) return;
  
  try {
    await fetch(`${API_URL}/api/comms/v3/notifications/message-reaction`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        recipient_id: messageAuthorId,
        reactor_id: reactorId,
        reactor_name: reactorName,
        reactor_avatar: reactorAvatar,
        conversation_id: conversationId,
        message_id: messageId,
        emoji: emoji,
        message_preview: messagePreview,
        church_id: churchId
      })
    });
  } catch (err) {
    console.error('Error creating message reaction notification:', err);
  }
}

/**
 * Add a reaction to a message
 */
export async function addReaction(conversationId, messageId, emoji, userId) {
  const docRef = doc(db(), COLLECTIONS.CONVERSATIONS, conversationId, COLLECTIONS.MESSAGES, messageId);

  // FieldPath, not a dotted string: any emoji may be a key now (2026-09-09) and
  // a dotted path would reject the keycap ones ('*️⃣' contains '*').
  await updateDoc(docRef, new FieldPath('reactions', emoji), arrayUnion(userId));
}

/**
 * Remove a reaction from a message
 */
export async function removeReaction(conversationId, messageId, emoji, userId) {
  const docRef = doc(db(), COLLECTIONS.CONVERSATIONS, conversationId, COLLECTIONS.MESSAGES, messageId);

  await updateDoc(docRef, new FieldPath('reactions', emoji), arrayRemove(userId));
}

/**
 * Toggle a reaction (add if not present, remove if present)
 * @param {Object} options - Additional options for notifications
 * @param {string} options.reactorName - Name of the user reacting
 * @param {string} options.reactorAvatar - Avatar URL of the reactor
 * @param {string} options.churchId - Church ID for notifications
 */
export async function toggleReaction(conversationId, messageId, emoji, userId, options = {}) {
  const docRef = doc(db(), COLLECTIONS.CONVERSATIONS, conversationId, COLLECTIONS.MESSAGES, messageId);
  const snapshot = await getDoc(docRef);
  
  if (!snapshot.exists()) return;
  
  const messageData = snapshot.data();
  const reactions = messageData.reactions || {};
  const usersWithReaction = reactions[emoji] || [];
  
  if (usersWithReaction.includes(userId)) {
    await removeReaction(conversationId, messageId, emoji, userId);
    return false; // Removed
  } else {
    await addReaction(conversationId, messageId, emoji, userId);
    
    // Send notification to message author
    if (options.reactorName && messageData.senderId) {
      createMessageReactionNotification(
        conversationId,
        messageId,
        emoji,
        userId,
        options.reactorName,
        options.reactorAvatar,
        messageData.senderId,
        messageData.text || 'a message',
        options.churchId
      );
    }
    
    return true; // Added
  }
}

/**
 * Delete a message for the current user only
 */
export async function deleteMessageForMe(conversationId, messageId, userId) {
  const docRef = doc(db(), COLLECTIONS.CONVERSATIONS, conversationId, COLLECTIONS.MESSAGES, messageId);
  
  await updateDoc(docRef, {
    deletedFor: arrayUnion(userId)
  });
}

/**
 * Delete a message for everyone (only within 1 hour of sending)
 */
export async function deleteMessageForEveryone(conversationId, messageId, userId) {
  const docRef = doc(db(), COLLECTIONS.CONVERSATIONS, conversationId, COLLECTIONS.MESSAGES, messageId);
  const snapshot = await getDoc(docRef);
  
  if (!snapshot.exists()) {
    throw new Error('Message not found');
  }
  
  const message = snapshot.data();
  
  // Check if user is the sender
  if (message.senderId !== userId) {
    throw new Error('You can only delete your own messages for everyone');
  }
  
  // Check if within time window
  const createdAt = message.createdAt?.toDate?.() || new Date(message.createdAt);
  const now = new Date();
  const timeDiff = now - createdAt;
  
  if (timeDiff > DELETE_FOR_EVERYONE_WINDOW) {
    throw new Error('You can only delete messages within 1 hour of sending');
  }

  // Capture Supabase URLs BEFORE clearing fields, so we can submit them to the
  // backend cleanup endpoint. Without this, deleted-for-everyone messages leave
  // orphaned media files in Supabase storage until the 90-day cleanup cron runs.
  const urlsToDelete = [];
  // Images are stored as {url, width, height} objects (older rows may be plain
  // strings). P2-d (DIV-023): the object form was never queued for cleanup.
  (message.images || []).forEach((u) => {
    const url = typeof u === 'string' ? u : u?.url;
    if (typeof url === 'string' && url.startsWith('http')) urlsToDelete.push(url);
  });
  if (message.voiceMemo?.url) urlsToDelete.push(message.voiceMemo.url);
  if (message.video?.url) urlsToDelete.push(message.video.url);
  if (message.video?.thumbnailUrl) urlsToDelete.push(message.video.thumbnailUrl);

  await updateDoc(docRef, {
    deletedForEveryone: true,
    text: '',
    images: null,
    voiceMemo: null,
    video: null,
    poll: null
  });

  // Fire-and-forget Supabase cleanup. Backend validates URLs belong to caller
  // (path must contain /{userId}/), so this is safe to call from the client.
  // We don't await — slow Supabase deletes shouldn't block the UI. The backend's
  // 90-day orphan-scan cron is the durability backstop if this request fails.
  if (urlsToDelete.length > 0) {
    const API = process.env.REACT_APP_BACKEND_URL;
    fetch(`${API}/api/comms/v3/messaging/delete-media?user_id=${encodeURIComponent(userId)}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ urls: urlsToDelete }),
    }).catch((err) => {
      // Silent — 90-day cron will catch this. Avoid console spam in production.
      console.debug('DM media cleanup queue failed (90-day cron will backstop):', err?.message);
    });
  }

  // Update conversation's last message preview to show "This message was deleted"
  await updateLastMessage(conversationId, {
    text: 'This message was deleted',
    senderId: message.senderId,
    senderName: message.senderName,
    type: MESSAGE_TYPES.TEXT,
    createdAt: message.createdAt
  });
}


/**
 * Edit a message (only text, only own messages, only within 1 hour of sending)
 */
export async function editMessage(conversationId, messageId, userId, newText) {
  const docRef = doc(db(), COLLECTIONS.CONVERSATIONS, conversationId, COLLECTIONS.MESSAGES, messageId);
  const snapshot = await getDoc(docRef);

  if (!snapshot.exists()) {
    throw new Error('Message not found');
  }

  const message = snapshot.data();

  if (message.senderId !== userId) {
    throw new Error('You can only edit your own messages');
  }

  if (message.deletedForEveryone) {
    throw new Error('Cannot edit a deleted message');
  }

  const createdAt = message.createdAt?.toDate?.() || new Date(message.createdAt);
  const now = new Date();
  if (now - createdAt > DELETE_FOR_EVERYONE_WINDOW) {
    throw new Error('You can only edit messages within 1 hour of sending');
  }

  const trimmed = (newText || '').trim();
  if (!trimmed) {
    throw new Error('Message cannot be empty');
  }

  await updateDoc(docRef, {
    text: trimmed,
    edited: true,
    editedAt: serverTimestamp(),
  });

  // Update conversation preview only if this is the most recent message
  const conv = await getConversation(conversationId);
  const lastTs = conv?.lastMessage?.timestamp?.toDate?.() || new Date(0);
  const msgTs = createdAt;
  if (msgTs >= lastTs) {
    await updateLastMessage(conversationId, {
      text: trimmed,
      senderId: message.senderId,
      senderName: message.senderName,
      type: MESSAGE_TYPES.TEXT,
      createdAt: message.createdAt,
    });
  }
}

/**
 * Check if a message should be visible to a user
 * Note: deletedForEveryone messages ARE visible (shown as "This message was deleted")
 * Only deletedFor (delete for me) messages are hidden from specific users
 */
export function isMessageVisible(message, userId) {
  // deletedForEveryone messages should still be visible - they show as "This message was deleted"
  // Only hide messages that were deleted specifically for this user
  if (message.deletedFor?.includes(userId)) return false;
  return true;
}

/**
 * Get read status for a message
 */
export function getReadStatus(message, conversationParticipants, senderId) {
  const readBy = message.readBy || [];
  const otherParticipants = conversationParticipants.filter(id => id !== senderId);
  
  if (otherParticipants.length === 0) return 'sent';
  
  const readByOthers = otherParticipants.filter(id => readBy.includes(id));
  
  if (readByOthers.length === 0) return 'delivered';
  if (readByOthers.length === otherParticipants.length) return 'read';
  return `read_by_${readByOthers.length}`;
}
